"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
const stages = [{
  number: "01",
  eyebrow: "STAGE ONE — ASSESSMENT",
  title: "Understanding, before the movement begins.",
  body: "Every transformation starts with a baseline. We analyze your current physical state, your movement patterns, and your lifestyle goals. Whether you are a beginner or an athlete, we treat your body as a complex system to be optimized, not just a set of metrics to be tracked. We listen to your history to build a foundation that lasts.",
  illustration: "assessment_foundation"
}, {
  number: "02",
  eyebrow: "STAGE TWO — PROGRAMMING",
  title: "A plan built by science, by precision.",
  body: "Coach Kira and her team design your roadmap. Every exercise is selected with intent — considering biomechanics, recovery, and your specific physiological needs. We curate your training sessions like a high-performance protocol, adjusting volume and intensity until the program reads as one continuous, logical progression toward your peak.",
  illustration: "herb"
}, {
  number: "03",
  eyebrow: "STAGE THREE — EXECUTION",
  title: "The effort, the form, the focus.",
  body: "During your sessions, we prioritize technical mastery. We calibrate your movements, refine your posture, and ensure every repetition is performed with purpose. The pace is deliberate; the rest periods are as vital as the work itself. You are not just training; you are learning how to command your own strength with absolute confidence.",
  illustration: "flame"
}, {
  number: "04",
  eyebrow: "STAGE FOUR — EVOLUTION",
  title: "What remains, when the session ends.",
  body: "After each cycle, we return to the data. We review what improved, what challenged you, and what needs to be pushed further. A progress report, a refined nutrition strategy, a new set of movement goals — these accompany our next steps. The journey does not end; it merely evolves, until the next level of performance calls you back to the gym.",
  illustration: "linen"
}];
function StageIllustration({
  type
}: {
  type: string;
}) {
  switch (type) {
    case "assessment_foundation":
      return <svg viewBox="0 0 200 200" className="w-full h-auto max-w-[300px] mx-auto lg:mx-0 fill-none stroke-current stroke-[1.5]">
          <rect x="40" y="40" width="120" height="120" className="opacity-20" />
          <path d="M100 20V180M20 100H180" className="stroke-primary" />
          <circle cx="100" cy="100" r="60" />
          <circle cx="100" cy="100" r="30" className="stroke-primary" />
          <path d="M40 40L160 160M160 40L40 160" className="opacity-20" />
        </svg>;
    case "herb":
      return <svg viewBox="0 0 200 200" className="w-full h-auto max-w-[300px] mx-auto lg:mx-0 fill-none stroke-current stroke-[1.5]">
          <path d="M100 180C100 180 100 100 160 40" className="stroke-primary" />
          <path d="M100 180C100 180 100 100 40 40" />
          <path d="M100 140C130 120 150 90 150 60" />
          <path d="M100 140C70 120 50 90 50 60" className="stroke-primary" />
          <line x1="100" y1="180" x2="100" y2="40" className="opacity-30" />
        </svg>;
    case "flame":
      return <svg viewBox="0 0 200 200" className="w-full h-auto max-w-[300px] mx-auto lg:mx-0 fill-none stroke-current stroke-[1.5]">
          <path d="M100 180C140 180 170 150 170 110C170 70 100 20 100 20C100 20 30 70 30 110C30 150 60 180 100 180Z" />
          <path d="M100 160C125 160 145 140 145 115C145 90 100 50 100 50C100 50 55 90 55 115C55 140 75 160 100 160Z" className="stroke-primary" />
          <path d="M100 140C115 140 125 130 125 115C125 100 100 80 100 80C100 80 75 100 75 115C75 130 85 140 100 140Z" />
        </svg>;
    case "linen":
      return <svg viewBox="0 0 200 200" className="w-full h-auto max-w-[300px] mx-auto lg:mx-0 fill-none stroke-current stroke-[1.5]">
          <path d="M40 60H160M40 100H160M40 140H160" className="stroke-primary" />
          <path d="M60 40V160M100 40V160M140 40V160" className="opacity-30" />
          <rect x="40" y="40" width="120" height="120" />
        </svg>;
    default:
      return null;
  }
}
function ProcessStage({
  stage,
  index
}: {
  stage: (typeof stages)[number];
  index: number;
}) {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: artRef,
    isVisible: artVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div data-index={index} className="relative min-h-screen flex items-center py-20 lg:py-0">
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div ref={artRef} className={`lg:col-span-5 lg:col-start-1 text-muted-foreground order-2 lg:order-1 transition-all duration-[1000ms] ease-out ${artVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <StageIllustration type={stage.illustration} />
          </div>

          <div ref={textRef} className={`lg:col-span-5 lg:col-start-7 order-1 lg:order-2 transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${textVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16"}`}>
            <div className="font-default text-muted leading-none mb-6 lg:mb-8" style={{
            fontSize: "clamp(5rem, 10vw, 9rem)"
          }}>
              {stage.number}
            </div>
            <div className="font-sans text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
              {stage.eyebrow}
            </div>
            <h3 className="font-default font-semibold text-foreground leading-[1.1] mb-8" style={{
            fontSize: "clamp(1.75rem, 3.2vw, 2.75rem)"
          }}>
              {stage.title.split(",").map((part, i, arr) => <span key={i}>
                  {i === arr.length - 1 ? <em className="italic font-normal">{part}</em> : <>{part},</>}
                </span>)}
            </h3>
            <p className="font-default text-lg leading-[1.7] text-foreground/80 max-w-xl">
              {stage.body}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
export default function YourTransformationRoadmap() {
  const sectionRef = useRef<HTMLElement>(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [indicatorVisible, setIndicatorVisible] = useState(false);
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const viewportHeight = window.innerHeight;
      if (sectionTop < viewportHeight * 0.5 && sectionTop > -sectionHeight + viewportHeight) {
        setIndicatorVisible(true);
      } else {
        setIndicatorVisible(false);
      }
      const stageElements = section.querySelectorAll("[data-stage-index]");
      stageElements.forEach((el, idx) => {
        const stageRect = el.getBoundingClientRect();
        if (stageRect.top < viewportHeight * 0.5 && stageRect.bottom > viewportHeight * 0.5) {
          setCurrentStage(idx + 1);
        }
      });
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="4299" ref={sectionRef} id="your-transformation-roadmap" className="relative bg-background text-foreground overflow-hidden">
      <div className={`hidden lg:block fixed left-6 xl:left-10 top-1/2 -translate-y-1/2 z-20 transition-opacity duration-700 ${indicatorVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="flex flex-col items-start gap-3">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-muted-foreground [writing-mode:vertical-rl] rotate-180">
            Progress
          </span>
          <div className="font-default text-sm tracking-wider text-foreground">
            <span className="text-primary">
              {String(currentStage).padStart(2, "0")}
            </span>
            <span className="text-muted-foreground"> / 04</span>
          </div>
          <div className="flex flex-col gap-2 mt-2">
            {stages.map((_, i) => <div key={i} data-index={i} className={`w-px h-8 transition-all duration-500 ${i + 1 <= currentStage ? "bg-primary" : "bg-border"}`} />)}
          </div>
        </div>
      </div>

      <div className="relative pt-24 lg:pt-40 pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div ref={introRef} className={`lg:col-span-3 lg:col-start-1 transition-all duration-[1000ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="font-sans text-xs tracking-[0.3em] uppercase text-muted-foreground lg:pt-6">
                — Our Method
              </div>
            </div>

            <div className="lg:col-span-9 lg:col-start-4">
              <h2 className={`font-default font-extrabold text-foreground leading-[0.95] tracking-tighter transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              fontSize: "clamp(3rem, 8vw, 6rem)"
            }}>
                <span className="block overflow-hidden">
                  <span className={`block transition-transform duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${introVisible ? "translate-y-0" : "translate-y-full"}`}>
                    The path
                  </span>
                </span>
                <span className="block overflow-hidden">
                  <span className={`block transition-transform duration-[1000ms] delay-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${introVisible ? "translate-y-0" : "translate-y-full"}`}>
                    to <em className="italic font-normal">transformation.</em>
                  </span>
                </span>
              </h2>
            </div>

            <div className="lg:col-span-4 lg:col-start-4 mt-12 lg:mt-16">
              <p className={`font-default text-lg lg:text-xl leading-[1.65] text-foreground/80 transition-all duration-[1200ms] delay-500 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Each journey unfolds in four deliberate phases —
                Assessment, Programming, Execution, and Evolution. We do not
                rush. We do not guess. We build, the way a high-performance
                athlete is built, with every session answering the last.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-border" />

      <div className="relative">
        {stages.map((stage, i) => <div key={i} data-stage-index={i} data-index={i}>
            <ProcessStage stage={stage} index={i} />
            {i < stages.length - 1 && <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <Separator className="bg-border" />
              </div>}
          </div>)}
      </div>
    </section>;
}